import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  fetchAuthConfig,
  initialiseOidc,
  signIn,
  signOut,
  accessToken,
  activeAccount,
  type AuthConfig,
} from './auth';
import { useBearerTokens } from './api';
import { Logo } from '../components/Logo';

/**
 * Decides how this deployment authenticates, before anything calls the API.
 *
 * In development it steps aside entirely and the user switcher works as before.
 * Under SSO it completes the Entra redirect, installs the token provider the
 * API client uses, and only then renders the app — so no request is ever made
 * without a token, which would otherwise surface as a spurious 401 on first
 * paint.
 */

type State =
  | { status: 'loading' }
  | { status: 'dev' }
  | { status: 'ready'; name: string }
  | { status: 'signed-out' }
  | { status: 'failed'; error: string };

export interface AuthState {
  /** Present under SSO; absent in development mode. */
  signOut?: () => void;
  signedInAs?: string;
}

const AuthContext = createContext<AuthState>({});

/** Empty object in development mode — the user switcher applies instead. */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      let config: AuthConfig;
      try {
        config = await fetchAuthConfig();
      } catch (error) {
        if (!cancelled) {
          setState({ status: 'failed', error: (error as Error).message });
        }
        return;
      }

      if (config.mode === 'dev') {
        if (!cancelled) setState({ status: 'dev' });
        return;
      }

      try {
        const account = await initialiseOidc(config);
        if (cancelled) return;
        if (!account) {
          setState({ status: 'signed-out' });
          return;
        }
        useBearerTokens(accessToken);
        setState({ status: 'ready', name: account.name ?? account.username });
      } catch (error) {
        if (!cancelled) setState({ status: 'failed', error: (error as Error).message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return <Splash>Checking your sign-in…</Splash>;
  }

  if (state.status === 'failed') {
    return (
      <Splash title="Aura could not start">
        <p className="text-sm text-ink-2">{state.error}</p>
        <button className={BUTTON} onClick={() => window.location.reload()}>
          Try again
        </button>
      </Splash>
    );
  }

  if (state.status === 'signed-out') {
    return (
      <Splash>
        <p className="max-w-sm text-sm text-ink-2">
          Sign in with your SmartWinnr account to continue.
        </p>
        <button className={BUTTON} onClick={() => void signIn()}>
          Sign in with Microsoft
        </button>
      </Splash>
    );
  }

  if (state.status === 'dev') {
    return <AuthContext.Provider value={{}}>{children}</AuthContext.Provider>;
  }

  return (
    <AuthContext.Provider
      value={{
        signedInAs: activeAccount()?.username ?? state.name,
        signOut: () => void signOut(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const BUTTON =
  'mt-1 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90';

function Splash({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-page px-6 text-center">
      <Logo className="h-10" />
      {title && <span className="text-lg font-semibold text-ink">{title}</span>}
      {children}
    </div>
  );
}
